<template>
  <div class="verification-page">
    <!-- Header Section -->
    <section class="header">
      <h1>Verification Portal</h1>
    </section>

    <!-- Tabs -->
    <div class="verification-tabs">
      <button
        type="button"
        class="verification-tab"
        :class="{ active: activeTab === 'pre' }"
        @click="selectTab('pre')"
      >
        Pre-Verification
      </button>
      <button
        type="button"
        class="verification-tab"
        :class="{ active: activeTab === 'face' }"
        @click="selectTab('face')"
      >
        Face Verification
      </button>
    </div>

    <!-- Pre-Verification Tab -->
    <section v-show="activeTab === 'pre'">
      <div class="header">
        <ul>
          <li>
            It is crucial that you input all the details correctly, so please
            spend the time to read the
            <a href="https://mywhoosh.com/ruleset" target="_blank"
              >technical guide and ruleset</a
            >
            and be as thorough as possible.
          </li>
          <li>
            All videos must now be submitted as an unlisted YouTube link. No
            direct file submission will be accepted.
          </li>
        </ul>
      </div>

      <div class="steps-box">
        <!-- Loading state -->
        <div v-if="loading" class="loading-indicator">
          <p>Submitting your information...</p>
        </div>

        <form @submit.prevent="validateAndSubmit">
          <template v-if="!submitted">
            <!-- Step 1: Hardware -->
            <div class="form-section">
              <h3>1. Hardware Details</h3>

              <label for="smartTrainer">Primary Power Source<span class="text-danger">*</span></label>
              <select
                v-model="form.smartTrainer"
                :class="{ 'input-error': validationErrors.smartTrainer }"
              >
                <option disabled value="">Select Primary Power Source</option>
                <option
                  v-for="source in primaryPowerSources"
                  :key="source.name"
                  :value="source.name"
                >
                  {{ source.name }}
                </option>
              </select>
              <span v-if="validationErrors.smartTrainer" class="error-text">{{
                validationErrors.smartTrainer
              }}</span>

              <div v-for="field in additionalFields" :key="field.key">
                <div class="required-input">
                  <input
                    type="text"
                    v-model="additionalFieldValues[field.key]"
                    :placeholder="field.placeholder"
                    :class="{
                      'input-error': validationErrors['additionalFields.' + field.key],
                    }"
                  />
                  <span v-if="field.required !== false" class="text-danger required-star">*</span>
                </div>
                <span
                  v-if="validationErrors['additionalFields.' + field.key]"
                  class="error-text"
                >
                  {{ validationErrors["additionalFields." + field.key] }}
                </span>
              </div>

              <label for="secondaryPowerSource">Secondary Power Source<span class="text-danger">*</span></label>
              <input
                type="text"
                v-model="form.secondaryPowerSource"
                placeholder="Include make, model, serial number of secondary power source (pedals, crank, etc.)"
                :class="{ 'input-error': validationErrors.secondaryPowerSource }"
              />
              <span
                v-if="validationErrors.secondaryPowerSource"
                class="error-text"
              >
                {{ validationErrors.secondaryPowerSource }}
              </span>
            </div>

            <!-- Step 2: Weight/Height -->
            <div class="form-section">
              <h3>2. Share your Weight and Height Details</h3>
              <p class="step-description">
                Please note that the weight and height must match the weight and
                height in your videos.
              </p>

              <label for="weight">Weight (kg)<span class="text-danger">*</span></label>
              <input
                type="number"
                @wheel="$event.target.blur()"
                step="0.1"
                v-model.number="form.weight"
                placeholder="Weight (kg)"
                :class="{ 'input-error': validationErrors.weight }"
              />
              <span v-if="validationErrors.weight" class="error-text">{{
                validationErrors.weight
              }}</span>

              <label for="height">Height (cm)<span class="text-danger">*</span></label>
              <input
                type="number"
                @wheel="$event.target.blur()"
                v-model.number="form.height"
                placeholder="Height (cm)"
                :class="{ 'input-error': validationErrors.height }"
              />
              <span v-if="validationErrors.height" class="error-text">{{
                validationErrors.height
              }}</span>
            </div>

            <!-- Step 3: Video -->
            <div class="form-section">
              <h3>
                3. Share video proof for Smart Trainer, Height, Weight,
                Secondary Power Source and Power Passport Workout
              </h3>
              <p class="step-description">
                The accepted protocol for power passport text, weigh-in, height
                and equipment videos can be found in the
                <a href="https://mywhoosh.com/ruleset" target="_blank"
                  >MyWhoosh ruleset</a
                >
              </p>

              <label for="videoLink">Video Link<span class="text-danger">*</span></label>
              <input
                type="text"
                v-model="form.videoLink"
                placeholder="https://www.youtube.com/..."
                :class="{ 'input-error': validationErrors.videoLink }"
              />
              <span v-if="validationErrors.videoLink" class="error-text">{{
                validationErrors.videoLink
              }}</span>
            </div>

            <!-- Step 4: Strava -->
            <div class="form-section">
              <h3>4. Strava link<span class="text-danger">*</span></h3>

              <input
                type="text"
                v-model="form.stravaLink"
                placeholder="Your public strava link"
                :class="{ 'input-error': validationErrors.stravaLink }"
              />
              <span v-if="validationErrors.stravaLink" class="error-text">{{
                validationErrors.stravaLink
              }}</span>
            </div>

            <!-- Step 5: Message -->
            <div class="form-section">
              <h3>5. Message</h3>

              <textarea
                v-model="form.message"
                placeholder="Any message..."
              ></textarea>
            </div>

            <!-- Info Note -->
            <p class="note">
              ⚠ Please note that the verification process for all steps is
              expected to be completed within 72 hours.
            </p>

            <!-- Error display -->
            <div v-if="errors.length" class="error-messages">
              <h4>Please correct the following errors:</h4>
              <ul>
                <li
                  v-for="(error, index) in errors"
                  :key="index"
                  class="error-item"
                >
                  {{ error }}
                </li>
              </ul>
            </div>

            <!-- Buttons -->
            <div class="form-actions justify-end">
              <button type="submit" class="btn-submit" :disabled="loading">
                Submit
              </button>
            </div>
          </template>
        </form>

        <transition name="fade">
          <template v-if="submitted">
            <div class="success-message">
              <h3>Thank you for your submission!</h3>
              <p>
                Your pre-verification request has been received. We will review
                it within the next 12 hours.
              </p>
            </div>
          </template>
        </transition>
      </div>

      <div class="header">
        <p>
          For any issues with this form, please contact
          <a href="mailto:racecontrol@mywhoosh.com">racecontrol@mywhoosh.com</a>.
        </p>
      </div>
    </section>

    <!-- Face Verification Tab -->
    <section v-show="activeTab === 'face'">
      <div class="steps-box">
        <form @submit.prevent="validateAndSubmitFace">
          <template v-if="!faceSubmitted">
            <!-- Face Verification Video -->
            <div class="form-section">
              <p class="step-description face-intro">
                Upload a video that will be used as the reference for automated
                face verification during events. Ensure your face is clearly
                visible, well-lit, and remains fully within the camera frame
                throughout the recording.
              </p>

              <h4 class="face-steps-title">Steps</h4>
              <ol class="face-steps">
                <li>Face the camera and look directly ahead.</li>
                <li>Raise your head slightly, then return to the center.</li>
                <li>Lower your head slightly, then return to the center.</li>
                <li>Turn your head to the left, then return to the center.</li>
                <li>Turn your head to the right, then return to the center.</li>
                <li>
                  Ensure your video is 8–12 seconds long, preferably in MP4
                  format, and does not exceed 100 MB.
                </li>
                <li>
                  Upload your video using the field below and submit your Face
                  Verification request.
                </li>
              </ol>

              <label for="faceVideo">Video File<span class="text-danger">*</span></label>
              <input
                id="faceVideo"
                type="file"
                accept="video/*"
                @change="onFaceFileChange"
                :class="{ 'input-error': faceValidationErrors.faceVideo }"
              />
              <span
                v-if="faceValidationErrors.faceVideo"
                class="error-text"
                >{{ faceValidationErrors.faceVideo }}</span
              >

              <label for="faceMessage">Message</label>
              <textarea
                id="faceMessage"
                v-model="faceForm.message"
                placeholder="Any message..."
              ></textarea>
            </div>

            <!-- Info Note -->
            <p class="note">
              ⚠ Please note that the verification process is expected to be
              completed within 72 hours.
            </p>

            <!-- Error display -->
            <div v-if="faceErrors.length" class="error-messages">
              <h4>Please correct the following errors:</h4>
              <ul>
                <li
                  v-for="(error, index) in faceErrors"
                  :key="index"
                  class="error-item"
                >
                  {{ error }}
                </li>
              </ul>
            </div>

            <!-- Buttons -->
            <div class="form-actions justify-end">
              <button type="submit" class="btn-submit" :disabled="faceLoading">
                Submit
              </button>
            </div>
          </template>
        </form>

        <transition name="fade">
          <template v-if="faceSubmitted">
            <div class="success-message">
              <h3>Thank you for your submission!</h3>
              <p>
                Your face verification request has been received. We will review
                it within the next 12 hours.
              </p>
            </div>
          </template>
        </transition>
      </div>

      <div class="header">
        <p>
          For any issues with this form, please contact
          <a href="mailto:racecontrol@mywhoosh.com">racecontrol@mywhoosh.com</a>.
        </p>
      </div>
    </section>

    <!-- Upload progress popup -->
    <div v-if="faceLoading" class="upload-overlay">
      <div class="upload-dialog">
        <h3 class="upload-title">Uploading your video</h3>
        <div
          class="upload-progress"
          role="progressbar"
          :aria-valuenow="faceUploadProgress"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            class="upload-progress-bar"
            :style="{ width: faceUploadProgress + '%' }"
          ></div>
        </div>
        <p class="upload-status">{{ uploadStatusText }}</p>
        <p class="upload-hint">
          Please keep this window open until it finishes.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ValidationMixin from "@/mixins/src-event/ValidationMixin";

export default {
  name: "VerificationPage",
  mixins: [ValidationMixin],
  head: {
    title: {
      inner: "Verification Portal - MyWhoosh",
    },
    link: [
      {
        rel: "stylesheet",
        href: "/assets/css/src_event.css",
      },
    ],
  },
  data() {
    return {
      activeTab: "pre",
      form: {
        smartTrainer: "",
        secondaryPowerSource: "",
        weight: "",
        height: "",
        videoLink: "",
        stravaLink: "",
        message: "",
      },
      additionalFieldValues: {},
      validationErrors: {},
      errors: [],
      loading: false,
      submitted: false,
      verificationStatus: null,
      faceForm: {
        faceVideo: null,
        message: "",
      },
      faceValidationErrors: {},
      faceErrors: [],
      faceLoading: false,
      faceUploadProgress: 0,
      faceSubmitted: false,
    };
  },
  computed: {
    ...mapGetters("user", {
      verification_status: "verification_status",
    }),
    ...mapGetters("page", ["setting"]),
    primaryPowerSources() {
      return this.setting?.primary_power_sources || [];
    },
    additionalFields() {
      const source = this.primaryPowerSources.find(
        (item) => item.name === this.form.smartTrainer
      );
      return source?.additional_fields || [];
    },
    uploadStatusText() {
      return this.faceUploadProgress < 100
        ? this.faceUploadProgress + "% uploaded"
        : "Processing your submission…";
    },
  },
  created() {
    this.checkUserAndVerificationStatus();
    this.syncTabFromHash();
    if (!this.setting) {
      this.$store.dispatch("page/setting");
    }
  },
  methods: {
    ...mapActions("src_verification", [
      "saveVerificationData",
      "saveFaceVerificationData",
      "resetVerification",
    ]),

    ...mapActions("user", [
      "get_rider_default_data", // Add this to refresh data after submission
    ]),

    checkUserAndVerificationStatus() {
      try {
        // Check for login token in localStorage
        const token = localStorage.getItem("webToken");
        if (!token) {
          this.safeNavigate("/login");
          return;
        }

        // Use Vuex getter instead of localStorage
        this.verificationStatus = this.verification_status;

        // Allow access for 'not_submitted' and 'rejected' statuses
        if (this.verificationStatus === "pending") {
          this.safeNavigate("/user/activities#profile");
        } else if (this.verificationStatus === "approved") {
          this.safeNavigate("/user/activities#profile");
        }
        // Allow 'rejected' and 'not_submitted' users to access the form
      } catch (error) {
        this.safeNavigate("/login");
      }
    },

    // Helper method to safely navigate
    safeNavigate(route) {
      const currentPath = this.$route.path + (this.$route.hash || "");
      if (currentPath !== route) {
        this.$router.push(route).catch((err) => {
          // Ignore navigation duplicated errors
          if (err.name !== "NavigationDuplicated") {
            console.error("Navigation error:", err);
          }
        });
      }
    },

    // Map between the active tab and the URL hash.
    tabFromHash(hash) {
      return hash === "#face-verification" ? "face" : "pre";
    },

    hashFromTab(tab) {
      return tab === "face" ? "#face-verification" : "#pre-verification";
    },

    syncTabFromHash() {
      this.activeTab = this.tabFromHash(this.$route.hash);
    },

    selectTab(tab) {
      this.activeTab = tab;
      const hash = this.hashFromTab(tab);
      if (this.$route.hash !== hash) {
        this.$router.replace({ hash }).catch((err) => {
          if (err.name !== "NavigationDuplicated") {
            console.error("Navigation error:", err);
          }
        });
      }
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    },

    onFaceFileChange(event) {
      const file = event.target.files && event.target.files[0];
      this.faceForm.faceVideo = file || null;
      this.$delete(this.faceValidationErrors, "faceVideo");
    },

    validateFaceForm() {
      this.faceValidationErrors = {};
      this.faceErrors = [];

      const file = this.faceForm.faceVideo;
      const maxBytes = 100 * 1024 * 1024; // 100 MB

      if (!file) {
        this.faceValidationErrors.faceVideo = "A video file is required";
        this.faceErrors.push("A video file is required");
      } else if (!file.type || !file.type.startsWith("video/")) {
        this.faceValidationErrors.faceVideo = "Please upload a valid video file";
        this.faceErrors.push("Please upload a valid video file");
      } else if (file.size > maxBytes) {
        this.faceValidationErrors.faceVideo = "Video must not exceed 100 MB";
        this.faceErrors.push("Video must not exceed 100 MB");
      }

      return this.faceErrors.length === 0;
    },

    async validateAndSubmit() {
      if (!this.validateForm()) return;

      this.loading = true;
      try {
        const response = await this.saveVerificationData({
          ...this.form,
          additionalFields: this.additionalFieldValues,
        });

        const status = response?.data?.status;
        const message = response?.data?.message;

        if (status) {
          this.submitted = true;
          await this.get_rider_default_data();

          this.$nextTick(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          });

          setTimeout(() => {
            this.safeNavigate("/user/activities#profile");
          }, 3000);
        } else {
          this.errors = [
            message || "There was a problem submitting your data.",
          ];
        }
      } catch (error) {
        this.handleValidationErrors(error, this.validationErrors, this.errors);
      } finally {
        this.loading = false;
      }
    },

    async validateAndSubmitFace() {
      if (!this.validateFaceForm()) return;

      this.faceLoading = true;
      this.faceUploadProgress = 0;
      try {
        const formData = new FormData();
        formData.append("faceVideo", this.faceForm.faceVideo);
        if (this.faceForm.message) {
          formData.append("message", this.faceForm.message);
        }

        const response = await this.saveFaceVerificationData({
          formData,
          onProgress: (percent) => {
            this.faceUploadProgress = percent;
          },
        });

        const status = response?.data?.status;
        const message = response?.data?.message;

        if (status) {
          this.faceSubmitted = true;

          this.$nextTick(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          });

          setTimeout(() => {
            this.safeNavigate("/user/activities#profile");
          }, 3000);
        } else {
          this.faceErrors = [
            message || "There was a problem submitting your data.",
          ];
        }
      } catch (error) {
        this.handleValidationErrors(
          error,
          this.faceValidationErrors,
          this.faceErrors
        );
      } finally {
        this.faceLoading = false;
      }
    },

    cancel() {
      const hasData = Object.values(this.form).some((value) => !!value);

      if (hasData) {
        const confirmCancel = window.confirm(
          "Are you sure you want to cancel? All your entered data will be lost."
        );
        if (confirmCancel) {
          this.resetForm();
          this.$router.go(-1);
        }
      } else {
        this.$router.go(-1);
      }
    },

    resetForm() {
      this.form = {
        smartTrainer: "",
        secondaryPowerSource: "",
        weight: "",
        height: "",
        videoLink: "",
        stravaLink: "",
        message: "",
      };
      this.additionalFieldValues = {};
      this.validationErrors = {};
      this.errors = [];
      this.resetVerification();
    },
  },
  beforeDestroy() {
    if (!this.submitted) {
      this.resetVerification();
    }
  },
  watch: {
    verification_status: {
      handler(newStatus, oldStatus) {
        // Only act if status actually changed
        if (newStatus !== oldStatus) {
          this.verificationStatus = newStatus;
          if (newStatus !== "not_submitted") {
            this.safeNavigate("/user/activities#profile");
          }
        }
      },
      immediate: true,
    },
    additionalFields(fields) {
      this.additionalFieldValues = Object.fromEntries(
        fields.map((field) => [field.key, ""])
      );
    },
    "$route.hash"(newHash) {
      this.activeTab = this.tabFromHash(newHash);
    },
  },
};
</script>

<style scoped>
.verification-tabs {
  display: flex;
  width: 100%;
  gap: 0;
  margin-bottom: 28px;
}

.verification-tab {
  flex: 1;
  background: #f3f2ee;
  border: none;
  border-radius: 0;
  padding: 0 16px;
  height: 44px;
  font-size: 15px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 0.2s linear, color 0.2s linear;
}

.required-input {
  position: relative;
}

.required-input .required-star {
  position: absolute;
  top: 8px;
  bottom: 0;
  right: 12px;
  width: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.form-section select {
  appearance: none;
  -webkit-appearance: none;
  padding-right: 36px;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' fill='none' stroke='%23333' stroke-width='1.5'/%3E%3C/svg%3E") no-repeat right 12px center / 12px 8px;
}

.verification-tab:hover {
  color: #374151;
}

.verification-tab:focus,
.verification-tab:focus-visible,
.verification-tab:active {
  outline: none;
  box-shadow: none;
  border: none;
}

.verification-tab.active {
  background: #fbb814;
  color: #fff;
}

/* Bullets and numbers share the same indent and consistent marker spacing;
   wrapped lines hang neatly under the text. */
.verification-page .header ul,
.verification-page .face-steps {
  margin: 12px 0;
  padding-left: 1.25em;
  list-style-position: outside;
}

.verification-page .face-steps {
  margin-bottom: 24px;
}

.verification-page .face-steps {
  list-style: decimal;
}

.verification-page .header ul li,
.verification-page .face-steps li {
  padding-left: 0.35em;
  margin-bottom: 8px;
  line-height: 1.5;
}

.verification-page .face-intro {
  font-size: 16px;
  color: #444;
  line-height: 1.5;
}

.face-steps-title {
  margin: 16px 0 8px;
  font-weight: 600;
}

/* Upload progress popup */
.upload-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.upload-dialog {
  background: #fff;
  border-radius: 12px;
  padding: 28px 32px;
  width: 90%;
  max-width: 380px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.upload-title {
  margin: 0 0 18px;
  font-size: 18px;
  font-weight: 700;
}

.upload-progress {
  height: 10px;
  background: #eee;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 12px;
}

.upload-progress-bar {
  height: 100%;
  background: #fbb814;
  border-radius: 999px;
  transition: width 0.2s ease;
}

.upload-status {
  margin: 0 0 6px;
  font-weight: 600;
  color: #333;
}

.upload-hint {
  margin: 0;
  font-size: 13px;
  color: #888;
}
</style>
